import { useEffect, useState } from 'react';
import axios from 'axios';
import imageGenerationService, { GeneratedImage, CreativeAnalysis } from '../services/api/imageGenerationService';

type AnalyzableImage = (GeneratedImage & { creative_analysis?: CreativeAnalysis | null }) | null | undefined;

interface UseCreativeAnalysisResult {
  analysis: CreativeAnalysis | null;
  isLoading: boolean;
  analysisError: string | null;
}

/**
 * Fetches (or reuses the already-persisted) AI creative analysis for a generated image.
 * Shared by BannerDetailPanel（AI効果予測＋制作意図）and CarouselImageViewerModal（制作意図のみ）
 * so both use the same fetch/cache/error behavior instead of duplicating it.
 *
 * `image` may be null/undefined (e.g. a viewer whose slide array just shrank to empty) —
 * the hook simply reports "not loading, no analysis" in that case instead of fetching.
 */
export function useCreativeAnalysis(image: AnalyzableImage): UseCreativeAnalysisResult {
  const [analysis, setAnalysis] = useState<CreativeAnalysis | null>(image?.creative_analysis ?? null);
  const [isLoading, setIsLoading] = useState(!!image && !image.creative_analysis);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  useEffect(() => {
    if (!image) {
      setAnalysis(null);
      setIsLoading(false);
      setAnalysisError(null);
      return;
    }

    if (image.creative_analysis) {
      setAnalysis(image.creative_analysis);
      setIsLoading(false);
      setAnalysisError(null);
      return;
    }

    let cancelled = false;

    const fetchAnalysis = async () => {
      try {
        setIsLoading(true);
        setAnalysisError(null);
        const result = await imageGenerationService.analyzeCreative(image.id);
        if (!cancelled) {
          setAnalysis(result);
        }
      } catch (err) {
        console.error('Failed to fetch creative analysis:', err);
        if (!cancelled) {
          // 503/429 = 一時的なAI混雑・分析実行中。再試行を案内する
          const status = axios.isAxiosError(err) ? err.response?.status : undefined;
          setAnalysisError(
            status === 503 || status === 429
              ? 'AI分析が混み合っています。しばらく待ってから再度お試しください。'
              : '分析データの取得に失敗しました'
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchAnalysis();

    return () => {
      cancelled = true;
    };
  }, [image?.id, image?.creative_analysis]);

  return { analysis, isLoading, analysisError };
}
